import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSeoOverride } from "@/components/seo/SeoContext";

const SITE_NAME = "MotoTrips";
const DEFAULT_IMAGE = "https://mototrips.net/og-invite.png";
const BASE_URL = "https://mototrips.net";

/**
 * Reusable SEO head component.
 * Usage: <SEO title="Clubes" description="..." path="/clubes" />
 *
 * 2026-06-15: this no longer emits its OWN title/description/OG/canonical/
 * hreflang/twitter <Helmet> — that caused DUPLICATE tags alongside the
 * global <SeoHead> (react-helmet-async@3.0.0 does not dedupe across
 * separate Helmet instances → the GSC "duplicate canonical" noise).
 * Instead it PUSHES its values up to <SeoHead> via useSeoOverride and only
 * renders JSON-LD (Organization + optional page schema), which is additive
 * and has no dedupe concern.
 */
const LOGO_URL = "https://mototrips.net/logo-mototrips.png";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": SITE_NAME,
  "url": BASE_URL,
  "logo": LOGO_URL,
  "image": LOGO_URL,
  "description": "Plataforma internacional para motociclistas partilharem passeios, rotas e aventuras — disponível nos principais idiomas (português, inglês, espanhol e francês) com tradução automática de textos.",
  "sameAs": [],
};

const SUPPORTED_LANGS = ["pt", "en", "es", "fr"];

export function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
  langs = SUPPORTED_LANGS,
}) {
  const { t } = useTranslation("common");
  const seoDescription = description || t("seo.site_description");
  const fullTitle = title ? `${title} | ${SITE_NAME}` : t("seo.site_title", { defaultValue: SITE_NAME });

  // Push to the single source of truth (<SeoHead>). `path` (when set) is the
  // clean canonical path; `langs` restricts hreflang/canonical for pages
  // offered in a subset of languages (e.g. PT-only).
  useSeoOverride({
    title: fullTitle,
    description: seoDescription,
    image,
    type,
    robots: noindex ? "noindex, nofollow" : null,
    path: path || null,
    langs,
  });

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
